import { computed, ref } from 'vue'
import type {
  ColorScheme,
  AuthenticatedUser,
  MaximumUploadBytesByAttachmentKind,
  NotificationSoundName,
  SupportedLocale,
} from '@threadline/shared'
import {
  fetchAuthenticationStatus,
  revokeAllSessions,
  revokeCurrentSession,
  updatePreferredColorScheme,
  updatePreferredLocale,
} from '../api/backendApiClient.ts'
import { setActiveAccountHandle } from '../api/activeAccount.ts'
import { setDevelopmentTools } from './useDevelopmentTools.ts'
import {
  forgetAccount,
  recordAccountUsed,
  refreshSavedAccountProfile,
} from './useAccountBook.ts'
import {
  applyActiveLocale,
  detectBrowserLocale,
  resolveDeploymentLocale,
} from '../i18n/index.ts'

const authenticatedUserReference = ref<AuthenticatedUser | null>(null)
const googleAuthenticationEnabledReference = ref(false)
const phoneAuthenticationEnabledReference = ref(false)
const magicLinkExpiryMinutesReference = ref(15)
const webPushPublicKeyReference = ref<string | null>(null)
const notificationSoundNameReference = ref<NotificationSoundName>('chime')
const defaultCallingCodeReference = ref<string | null>(null)
const authenticationStatusLoadedReference = ref(false)
const operatorPanelEnabledReference = ref(false)
const deploymentLocaleReference = ref<SupportedLocale | null>(resolveDeploymentLocale())
const maximumUploadBytesReference = ref<MaximumUploadBytesByAttachmentKind>({
  image: 52_428_800,
  audio: 104_857_600,
  video: 2_147_483_648,
  file: 524_288_000,
})

/**
 * A deployment that serves a single language always wins: there is nothing to choose and
 * nothing stored. Otherwise a stored preference wins over browser detection, and while the
 * preference is still unknown the interface follows the browser, which the backend
 * persists on first sign in.
 */
function applyLocaleForCurrentUser(): void {
  // The tab is named after the account it is acting as, so this runs wherever that
  // changes — signing in, leaving, and every reload of the status.
  const accountHandle = authenticatedUserReference.value?.handle ?? null
  if (deploymentLocaleReference.value !== null) {
    applyActiveLocale(deploymentLocaleReference.value, accountHandle)
    return
  }
  const preferredLocale = authenticatedUserReference.value?.preferredLocale ?? null
  applyActiveLocale(preferredLocale ?? detectBrowserLocale(), accountHandle)
}

/**
 * Loads the status of whichever account this tab is acting as. The active account is set
 * from the tab's own URL before this runs, so the request already carries that account's
 * token and the answer describes that account and no other.
 */
export async function loadAuthenticationStatus(): Promise<void> {
  const authenticationStatus = await fetchAuthenticationStatus()
  authenticatedUserReference.value = authenticationStatus.authenticatedUser
  googleAuthenticationEnabledReference.value = authenticationStatus.googleAuthenticationEnabled
  phoneAuthenticationEnabledReference.value = authenticationStatus.phoneAuthenticationEnabled
  // The shell already carried this, but a component mounted without one still learns the
  // deployment language here.
  deploymentLocaleReference.value =
    resolveDeploymentLocale() ?? authenticationStatus.deploymentLocale
  magicLinkExpiryMinutesReference.value = authenticationStatus.magicLinkExpiryMinutes
  webPushPublicKeyReference.value = authenticationStatus.webPushPublicKey
  notificationSoundNameReference.value = authenticationStatus.notificationSoundName
  maximumUploadBytesReference.value = authenticationStatus.maximumUploadBytesByAttachmentKind
  // What to assume for a phone number typed without a country code, decided by the
  // backend from this deployment's language and where the request came from.
  defaultCallingCodeReference.value = authenticationStatus.defaultCallingCode
  // `null` on any deployment that does not deliberately expose the development panel.
  setDevelopmentTools(authenticationStatus.developmentTools)
  // Decided by the server from its operator list: whether this session may open the
  // operator panel. Unsigned-out tabs read false, which hides the menu entry.
  operatorPanelEnabledReference.value = authenticationStatus.operatorPanelEnabled
  authenticationStatusLoadedReference.value = true

  const authenticatedUser = authenticationStatus.authenticatedUser
  if (authenticatedUser !== null) {
    // Keep the name and picture on the sign-in screen current, and remember that this is
    // the account this browser used last.
    refreshSavedAccountProfile(authenticatedUser.accountId, authenticatedUser)
    recordAccountUsed(authenticatedUser.accountId)
  }

  applyLocaleForCurrentUser()
}

/**
 * Leaves the account this tab is acting as. The token stays on the device on purpose: the
 * account keeps its place on the sign-in screen and is one click away again.
 */
export function leaveCurrentAccount(): void {
  authenticatedUserReference.value = null
  authenticationStatusLoadedReference.value = false
  operatorPanelEnabledReference.value = false
  setActiveAccountHandle(null)
  applyLocaleForCurrentUser()
}

/**
 * Removes the account from this device for good: the session is revoked on the server
 * first, so a copied token cannot outlive the removal, and only then forgotten here.
 */
export async function removeCurrentAccountFromThisDevice(): Promise<void> {
  const authenticatedUser = authenticatedUserReference.value
  try {
    await revokeCurrentSession()
  } finally {
    if (authenticatedUser !== null) {
      forgetAccount(authenticatedUser.accountId)
    }
    leaveCurrentAccount()
  }
}

/** Signs this account out of every device, this one included. */
export async function signOutOfEveryDevice(): Promise<number> {
  const authenticatedUser = authenticatedUserReference.value
  const revokedSessionCount = await revokeAllSessions()
  if (authenticatedUser !== null) {
    forgetAccount(authenticatedUser.accountId)
  }
  leaveCurrentAccount()
  return revokedSessionCount
}

export async function changePreferredLocale(preferredLocale: SupportedLocale): Promise<void> {
  authenticatedUserReference.value = await updatePreferredLocale(preferredLocale)
  applyLocaleForCurrentUser()
}

/**
 * Light or dark, or `null` to follow the device again. It is stored on the account, so the
 * other tab — holding somebody else — is unaffected by it.
 */
export async function changePreferredColorScheme(
  preferredColorScheme: ColorScheme | null,
): Promise<void> {
  authenticatedUserReference.value = await updatePreferredColorScheme(preferredColorScheme)
}

/** Forgets what was loaded so the next navigation reloads it for the account it names. */
export function resetAuthenticationStatus(): void {
  authenticatedUserReference.value = null
  authenticationStatusLoadedReference.value = false
}

export function useAuthenticationSession() {
  return {
    authenticatedUser: computed(() => authenticatedUserReference.value),
    isAuthenticated: computed(() => authenticatedUserReference.value !== null),
    googleAuthenticationEnabled: computed(() => googleAuthenticationEnabledReference.value),
    phoneAuthenticationEnabled: computed(() => phoneAuthenticationEnabledReference.value),
    magicLinkExpiryMinutes: computed(() => magicLinkExpiryMinutesReference.value),
    webPushPublicKey: computed(() => webPushPublicKeyReference.value),
    notificationSoundName: computed(() => notificationSoundNameReference.value),
    authenticationStatusLoaded: computed(() => authenticationStatusLoadedReference.value),
    operatorPanelEnabled: computed(() => operatorPanelEnabledReference.value),
    deploymentLocale: computed(() => deploymentLocaleReference.value),
    defaultCallingCode: computed(() => defaultCallingCodeReference.value),
    isMultilingualDeployment: computed(() => deploymentLocaleReference.value === null),
    maximumUploadBytesByAttachmentKind: computed(() => maximumUploadBytesReference.value),
  }
}
